/**
 * Shared chart theme for the financial charts.
 *
 * One source of truth for palette, typography and the axis/grid/legend defaults
 * every chart used to hardcode. Values are derived from the Amplinth brand and
 * validated for legibility + colour-vision-deficiency separation — never eyeball
 * a hex change here; re-run scripts/validate_palette.js (see the plan).
 *
 * NO JSX in this file — it stays importable from anywhere and avoids the
 * react-refresh/only-export-components rule. It exports plain frozen values and
 * pure helpers only (never hooks), which keeps the FinancialDashboardTab
 * hook-order hazard structurally unreachable from the theme layer.
 *
 * Recharts note: these are PROP BAGS meant to be spread onto real Recharts
 * elements (`<XAxis {...xAxisProps} />`). Recharts dispatches on child.type, so
 * wrapper components silently render nothing — never wrap, always spread.
 *
 * Scope: financial/charts + the EV/sensitivity surfaces. Industry and
 * visualizations charts adopt this in a later pass; nothing here is
 * financial-specific except EV_SEGMENT.
 */

// ── Ink & chrome ──────────────────────────────────────────────────────────────
// Brand navy is the app's ink, not a series colour: at OKLCH L 0.309 / C 0.055 it
// reads as near-black — right for titles/ticks, wrong for a fill.
export const CHART_INK = {
  title: '#262e4c',   // tailwind primary-500 / brand.navy
  label: '#4b5563',   // axis tick text
  muted: '#6b7280',   // reference-line labels, secondary values
  axis: '#9ca3af',    // axis lines, zero reference lines
  grid: '#ecebe8',    // tailwind neutral-200 / brand.cream — replaces #f0f0f0
  surface: '#ffffff',
  flagged: '#b45309', // amber-700 — the ⚠/★ tick colour
} as const;

export const CHART_TYPE = {
  axisTick: 11,
  legend: 11,
  dataLabel: 10,
  tooltip: 12,
  tickSmall: 10,
} as const;

// ── Palettes ──────────────────────────────────────────────────────────────────

/**
 * The focal company. The deep brand purple (tailwind `secondary-800`) — the
 * hero, drawn straight from the app palette so the charts read on-brand and
 * elegant. It dominates the lighter peer tints (see PEER_COLORS), reinforced by
 * the bold ★ tick and the legend. Never a peer, never a segment.
 */
export const FOCAL_COLOR = '#5d39a8'; // secondary-800 — deep brand purple

/**
 * Peers (line / grouped charts). A single-hue ramp of brand `secondary` tints,
 * all LIGHTER than the focal so the focal stays dominant; ordered medium→light
 * so peer lines stay legible. Fixed order — assign in sequence, never by rank,
 * never re-shuffled on filter. Identity is carried by lightness + the legend +
 * the ★ focal tick (see PEER_COLOR_SAFE_COUNT).
 *
 * NOTE: single-value peer BAR comparisons (Revenue CAGR, EV/EBITDA, IC CAGR,
 * D/E) deliberately do NOT use this ramp — they paint every peer one recessive
 * tone (PEER_BAR_UNIFORM) so only the focal stands out; the x-axis labels carry
 * identity there.
 */
// Order alternates lightness (mid / light / deep / …) rather than a smooth
// ramp: on the multi-line/grouped charts that means adjacent peers separate
// cleanly (validated — focal + first 3 peers clear the CVD and normal-vision
// ΔE floors), while still being nothing but brand `secondary` tints.
export const PEER_COLORS = [
  '#9679e7', // secondary-500
  '#c9b8f4', // secondary-300
  '#7144c9', // secondary-700
  '#d5c9f7',
  '#ad91ed', // secondary-400
  '#8154dd', // secondary-600
  '#e1d7f9', // secondary-200
  '#b39ddf',
] as const;

/**
 * The single tone for EVERY bar in a single-value peer comparison — focal and
 * peers alike. The focal is identified by its bold ★ axis tick (focalTickFactory),
 * not by its fill, which matches the EV Insights stacked charts (every company
 * the same colour, focal marked only on the axis). Same value as PEER_COLORS[0]
 * / the single-company trend bars, so all non-stacked bars read as one family.
 */
export const PEER_BAR_UNIFORM = '#9679e7'; // secondary-500

/**
 * One shared bar-width cap for every bar/stack chart on the EV Insights and
 * Revenue & Capital tabs, so bar widths stay consistent whether a chart has one
 * bar or ten (maxBarSize is a cap — many-bar charts self-size below it).
 */
export const BAR_MAX_SIZE = 120;

/**
 * In a single-hue ramp, lightness cleanly separates FOCAL + the first ~3 peers.
 * Past that, lightness alone stops carrying identity — the legend, the ★ tick
 * and the table toggle carry it. An information-theoretic cap, not a palette bug.
 */
export const PEER_COLOR_SAFE_COUNT = 3;

export function peerColorAt(index: number): string {
  return PEER_COLORS[index % PEER_COLORS.length];
}

/**
 * EV decomposition segments. ONE definition, shared by EVBreakdownChart,
 * EVBreakdownPercentageChart and EVInsightChart — which used to disagree
 * (Growth was #10b981 in one and #8b5cf6 in another).
 *
 * A single brand-purple ramp (tailwind `secondary` 800→500→300), dark → light,
 * ordered by certainty: current operations darkest (most certain), market
 * turnaround lightest (most speculative). Same-hue lightness steps keep the
 * segments on-theme and distinguishable — including for colour-vision deficiency.
 * Not amber (that means "focal company"), and no off-brand teal.
 */
export const EV_SEGMENT = {
  ebit: '#5d39a8',   // secondary-800 — current operations (most certain)
  growth: '#9679e7', // secondary-500 — brand purple, future growth
  market: '#c9b8f4', // secondary-300 — market turnaround (most speculative)
  cap: FOCAL_COLOR,  // CAP annotation line — focal brand purple, single-company charts only
} as const;

/**
 * One colour per recurring metric shown TOGETHER on one chart (ROIC/WACC/NOPAT/
 * Invested Capital), so a metric never changes identity between charts. Unlike
 * PEER_COLORS (a single-hue ramp for one-metric-many-companies), these need
 * hue+lightness separation to stay readable as overlaid lines — kept within the
 * brand navy→purple→violet family, no off-brand teal/pink.
 */
export const METRIC_COLOR = {
  roic: '#5d39a8',             // secondary-800 — deep purple
  wacc: '#9679e7',             // secondary-500 — brand purple
  nopat: '#7144c9',            // secondary-700
  invested_capital: '#c9b8f4', // secondary-300 — light purple
} as const;

/**
 * Focal rule: FOCAL_COLOR (the deep brand purple) marks "the thing to look at" —
 * the focal company in a peer comparison, or the latest/current value in a
 * single-company chart. It is NOT a context colour: average/reference lines use
 * the neutral `refLineProps` (CHART_INK.axis), never the focal purple.
 */

// ── Recharts prop bags (spread onto real elements) ─────────────────────────────

export const gridProps = {
  strokeDasharray: '3 3',
  stroke: CHART_INK.grid,
} as const;

export const xAxisProps = {
  tick: { fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label },
  tickLine: false,
  axisLine: { stroke: CHART_INK.grid },
} as const;

// tabular-nums so tick columns don't jitter as digits change.
export const yAxisProps = {
  tick: {
    fontSize: CHART_TYPE.axisTick,
    fill: CHART_INK.label,
    style: { fontVariantNumeric: 'tabular-nums' },
  },
  tickLine: false,
  axisLine: false,
  width: 48,
} as const;

export const legendProps = {
  wrapperStyle: { fontSize: CHART_TYPE.legend },
  iconType: 'square' as const,
} as const;

export const zeroLineProps = {
  stroke: CHART_INK.axis,
  strokeDasharray: '3 3',
} as const;

export const refLineProps = {
  stroke: CHART_INK.axis,
  strokeDasharray: '4 4',
  strokeWidth: 1.5,
} as const;

export const barRadius = [3, 3, 0, 0] as const;
